/** Browser binding for session ownership, durable recovery checkpoints and unload transport.
 * Rust decides whether an account/session checkpoint may replay. Web Locks prevent two tabs
 * from controlling the same activity; localStorage survives abrupt termination. Native unload
 * dialogs are browser-controlled and cannot offer a custom save form.
 * @module apps/web/src/lib/sessionLifecycle
 */
import { API_BASE, api } from './api';
import { fluxAgentBrowserCore, loadFluxAgentBrowser } from './fluxAgentBrowser';

/** Binding: obtain the browser's exclusive lock until the caller releases it. */
export function claimSession(owner: string, activity: string): Promise<(() => void) | null> {
  return new Promise((resolve, reject) => {
    if (!navigator.locks) { reject(new Error('This browser cannot safely coordinate timers across tabs.')); return; }
    void navigator.locks.request(`fluxability-session:${owner}:${activity}`, { ifAvailable: true }, lock => {
      if (!lock) { resolve(null); return; }
      return new Promise<void>(release => resolve(release));
    }).catch(reject);
  });
}

/** Binding: replay an owned durable checkpoint before restoring the server's timer state. */
export async function recoverSession(owner: string, session: string): Promise<{
  /** Whether an owned saved checkpoint was recovered. */
  recovered: boolean;
  /** Opaque form values restored only for the current owner and session. */
  draft?: Record<string, unknown>;
  /** True when browser storage could not be read. */
  unavailable?: boolean;
}> {
  await loadFluxAgentBrowser();
  const key = `fluxability-session:${owner}:${session}`;
  let raw: string;
  try { raw = localStorage.getItem(key) ?? 'null'; } catch { return { recovered: false, unavailable: true }; }
  const saved = JSON.parse(fluxAgentBrowserCore().session_checkpoint(raw, owner, session));
  const body = JSON.parse(fluxAgentBrowserCore().session_recovery(raw, owner, session));
  if (!body) return { recovered: Boolean(saved), draft: saved?.draft };
  const response = await api.training.pauseImmersion(session, 0, body.reason, body);
  if (!response.success) throw new Error(response.error?.message ?? 'Could not recover the paused timer.');
  return { recovered: true, draft: saved?.draft };
}

/** Binding: observe browser close and heartbeat instants while the owner holds its lock.
 * The checkpoint remains replayable until explicitly finished/cancelled or replaced after restore. */
export function bindSessionLifecycle(owner: string, session: string, running: () => boolean, draft: () => unknown, currentOwner: () => string | null, unavailable: () => void): () => void {
  const storageKey = `fluxability-session:${owner}:${session}`;
  const checkpoint = () => {
    const value = { owner, session, running: running(), draft: draft(), at: new Date().toISOString(), key: crypto.randomUUID() };
    const bounded = fluxAgentBrowserCore().session_checkpoint(JSON.stringify(value), currentOwner() ?? '', session);
    if (bounded === 'null') return null;
    try { localStorage.setItem(storageKey, bounded); } catch { unavailable(); }
    return value;
  };
  const beforeUnload = (event: BeforeUnloadEvent) => {
    event.preventDefault();
    event.returnValue = '';
  };
  const pageHide = () => {
    window.removeEventListener('beforeunload', beforeUnload);
    const value = checkpoint();
    if (!value) return;
    const body = fluxAgentBrowserCore().session_recovery(JSON.stringify(value), owner, session);
    if (body === 'null') return;
    let token: string | null = null;
    try { token = localStorage.getItem('flexiplanner-session-token'); } catch { unavailable(); }
    void fetch(`${API_BASE}/training/immersion/${encodeURIComponent(session)}/pause`, {
      method: 'POST', credentials: 'include', keepalive: true,
      headers: { 'Content-Type': 'application/json', 'X-Fluxability-CSRF': '1', 'Idempotency-Key': value.key, ...(token ? { Authorization: `Bearer ${token}` } : {}) }, body,
    }).catch(() => undefined);
  };
  const pageShow = (event: PageTransitionEvent) => { if (event.persisted) location.reload(); };
  checkpoint();
  const interval = window.setInterval(checkpoint, 1000);
  window.addEventListener('beforeunload', beforeUnload);
  window.addEventListener('pagehide', pageHide);
  window.addEventListener('pageshow', pageShow);
  return () => {
    clearInterval(interval);
    window.removeEventListener('beforeunload', beforeUnload);
    window.removeEventListener('pagehide', pageHide);
    window.removeEventListener('pageshow', pageShow);
  };
}

/** Binding: remove the durable record only after server-confirmed completion/cancellation. */
export function clearSessionCheckpoint(owner: string, session: string): void {
  try { localStorage.removeItem(`fluxability-session:${owner}:${session}`); } catch { /* Server completion remains authoritative if browser storage is unavailable. */ }
}
